import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { InlineEditableItem, AddInlineItem } from "@/components/InlineEditable";
import { ArrowRight } from "lucide-react";

/**
 * Renders custom home sections stored in site-config.custom_sections.
 * In admin edit mode, each section shows an inline WordPress-style toolbar and
 * a floating "+ Aggiungi sezione" button is displayed at the end so admins can
 * add brand new sections between hero and the rest of the home.
 */
export default function CustomHomeSections() {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    api.get("/site-config").then((r) => {
      setSections(Array.isArray(r.data?.custom_sections) ? r.data.custom_sections : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const check = () => setEditMode(document.body.classList.contains("admin-edit-mode"));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  const save = async (next) => {
    setSections(next);
    try {
      await api.put("/admin/site-config", { custom_sections: next });
      toast.success("Sezione salvata");
    } catch {
      toast.error("Errore salvataggio sezione");
    }
  };

  const update = (i, field, value) => save(sections.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  const del = (i) => save(sections.filter((_, idx) => idx !== i));
  const add = () => {
    const s = {
      id: `sec-${Date.now().toString(36)}`,
      title: "Nuova sezione",
      subtitle: "Sottotitolo",
      body: "Descrivi qui il contenuto della sezione. Puoi cambiare titolo e foto con la toolbar.",
      cta: "Scopri di più",
      href: "/catalogo",
      img: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80",
    };
    save([...sections, s]);
  };

  if (!sections.length && !(user?.is_admin && editMode)) return null;

  return (
    <section data-testid="custom-sections" className="py-16 md:py-20 bg-white border-y border-black/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-10">
        {sections.map((s, i) => (
          <InlineEditableItem
            key={s.id || i}
            title={s.title}
            image={s.img}
            label="sezione"
            onEditTitle={(v) => update(i, "title", v)}
            onEditImage={(v) => update(i, "img", v)}
            onDelete={() => del(i)}
          >
            <div data-testid={`custom-section-${s.id || i}`} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-white border border-black/10 overflow-hidden">
              <div className="aspect-video md:aspect-auto md:h-full overflow-hidden bg-black">
                {s.img && <img src={s.img} alt={s.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-8 md:p-12">
                {s.subtitle && (
                  <div className="text-[#22ff66] text-xs uppercase tracking-[0.3em] font-semibold mb-3">{s.subtitle}</div>
                )}
                <h2 className="heading-uc text-3xl md:text-4xl mb-4">{s.title}</h2>
                {s.body && <p className="text-black/70 mb-6 leading-relaxed">{s.body}</p>}
                {s.cta && s.href && (
                  <a href={s.href} className="inline-flex items-center gap-2 bg-[#22ff66] hover:bg-[#4EFF87] text-black px-6 py-3 uppercase tracking-widest font-semibold text-sm transition">
                    {s.cta} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </InlineEditableItem>
        ))}
        <div className="flex justify-center">
          <AddInlineItem testid="add-custom-section" label="+ Aggiungi Sezione" onClick={add} aspect="min-h-[120px] w-full" />
        </div>
      </div>
    </section>
  );
}
